import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { profileApi } from 'redux/Api/profileApi/ProfileApi';
import { getOrderAmountErrorMessage } from 'components/OrderAmountError/getOrderAmountErrorMessage';
import { resolveWorkerLimitError } from 'components/OrderAmountError/resolveWorkerLimitError';

interface ValidationErrors {
  type: string | null;
  description: string | null;
  amount: string | null;
  address: string | null;
  employee: string | null;
}

interface ValidationParams {
  selectedWorker?: any;
}

export const useOrderValidation = (params: ValidationParams = {}) => {
  const { selectedWorker } = params;
  const phone = localStorage.getItem('phone');
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const userInfo = useSelector(profileApi.endpoints.getInfoUser.select());

  const [errors, setErrors] = useState<ValidationErrors>({
    type: null,
    description: null,
    amount: null,
    address: null,
    employee: null,
  });

  const getMinMaxPrice = (phone: string) => {
    if (phone === '9052264047' || phone === '79052264047') {
      return { min: 50, max: 200000 };
    }
    return {
      min: userInfo?.data?.order_limits?.min ?? 0,
      max: userInfo?.data?.order_limits?.max ?? 0,
    };
  };

  const validateAmount = (amount: number): string | null => {
    const { min, max } = getMinMaxPrice(phone || '');

    if (!amount) {
      return `Минимальная сумма заказа ${min} рублей`;
    }

    const workerLimitError = resolveWorkerLimitError(amount, selectedWorker);

    if (workerLimitError) {
      return getOrderAmountErrorMessage({
        balance: 1,
        insufficientBalanceError: false,
        activeLimitExceeded: workerLimitError.activeLimitExceeded,
        activeLimitType: workerLimitError.activeLimitType,
        activeLimitError: workerLimitError.activeLimitError,
        activeLimitRemaining: workerLimitError.activeLimitRemaining,
        platformLimitError: false,
        min,
        max,
        isUnitBased: true,
      });
    }

    if (amount < min) {
      return `Минимальная сумма заказа ${min} рублей`;
    }
    if (amount > max) {
      return `Максимальная сумма заказа ${max.toLocaleString('ru-RU')} рублей`;
    }

    return null;
  };

  const validateFields = () => {
    const newErrors = {
      description: !infoOrderDetail.description?.trim()
        ? 'Введи описание'
        : infoOrderDetail.description.trim().length < 10
        ? 'Описание должно содержать минимум 10 символов'
        : null,
      amount: validateAmount(Number(infoOrderDetail.total_price)),
      address:
        infoOrderDetail.is_remote !== 'Удаленно' && !infoOrderDetail.city
          ? 'Выбери адрес из списка'
          : null,
      employee: !infoOrderDetail.worker_id ? 'Выбери исполнителя для заказа' : null,
      type:
        !infoOrderDetail.order_type_id || Number(infoOrderDetail.order_type_id) <= 0
          ? 'Выбери категорию услуг'
          : null,
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === null);
  };

  const validateDraftAmount = () => {
    if (infoOrderDetail.is_unit_based) {
      setErrors((prev) => ({ ...prev, amount: null }));
      return true;
    }

    const { min, max } = getMinMaxPrice(phone || '');

    const amountError = !infoOrderDetail.total_price
      ? `Минимальная сумма заказа ${min} рублей`
      : Number(infoOrderDetail.total_price) < min
      ? `Минимальная сумма заказа ${min} рублей`
      : Number(infoOrderDetail.total_price) > max
      ? `Максимальная сумма заказа ${max.toLocaleString('ru-RU')} рублей`
      : null;

    setErrors((prev) => ({ ...prev, amount: amountError }));
    return !amountError;
  };

  const clearError = useCallback((field: keyof ValidationErrors) => {
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const clearErrorForField = useCallback(
    (key: string | number) => {
      const field = String(key);
      if (field === 'description') clearError('description');
      if (field === 'amount') clearError('amount');
      if (field === 'order_type_id' || field === 'order_type') clearError('type');
    },
    [clearError],
  );

  return {
    errors,
    validateFields,
    validateDraftAmount,
    clearError,
    clearErrorForField,
  };
};
